import type { PolicyDeclarationFilesModalProps } from '../../types/modals'
import { useMemo, useState } from 'react'
import { Download, Eye, FileText, Info, Loader2 } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import type { PolicyDeclarationFileRow } from '../../services/applicationService'
import { showApiError } from '@/lib/utils/notifications'
import {
  openServeFileInNewTab,
  triggerServeFileDownload,
} from '@/lib/utils/serveFileHelpers'
import {
  formatAccessConfirmationLabelsFromFilename,
  sortPolicyDeclarationFilesNewestFirst,
} from '@/lib/utils/formatters'

const tid = 'application-modal-policy-declaration-files'

const viewBtnClass =
  'inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-[#1597ff] bg-white px-3 text-sm font-semibold text-[#1597ff] transition hover:bg-[#eef7ff] disabled:pointer-events-none disabled:opacity-60'

const downloadBtnClass =
  'inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-[#1597ff] bg-[#1597ff] px-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0d7acc] disabled:pointer-events-none disabled:opacity-60'

function getPolicyDeclarationFileId(file: PolicyDeclarationFileRow): string {
  return file.path || file.stream_url || file.filename || ''
}

export function PolicyDeclarationFilesModal({
  isOpen,
  onClose,
  files,
}: PolicyDeclarationFilesModalProps) {
  const [processing, setProcessing] = useState<Record<string, 'view' | 'download'>>({})
  const sortedFiles = useMemo(
    () => sortPolicyDeclarationFilesNewestFirst(files),
    [files],
  )

  const setProcessingKind = (id: string, kind: 'view' | 'download' | null) => {
    setProcessing((prev) => {
      if (!kind) {
        const { [id]: _removed, ...rest } = prev
        return rest
      }
      return { ...prev, [id]: kind }
    })
  }

  const viewFile = async (file: PolicyDeclarationFileRow) => {
    const id = getPolicyDeclarationFileId(file)
    if (!file.stream_url) return
    setProcessingKind(id, 'view')
    try {
      await openServeFileInNewTab(file.stream_url)
    } catch (error) {
      showApiError(error)
    } finally {
      setProcessingKind(id, null)
    }
  }

  const downloadFile = async (file: PolicyDeclarationFileRow) => {
    const id = getPolicyDeclarationFileId(file)
    if (!file.stream_url) return
    setProcessingKind(id, 'download')
    try {
      await triggerServeFileDownload(
        file.stream_url,
        file.filename || 'terms-and-conditions.pdf',
      )
    } catch (error) {
      showApiError(error)
    } finally {
      setProcessingKind(id, null)
    }
  }

  const count = sortedFiles.length
  const subtitle =
    count === 1 ? '1 confirmation document' : `${count} confirmation documents`

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Access Confirmations"
      subtitle={subtitle}
      onClose={onClose}
      maxWidth="max-w-2xl"
      bodyScrollOnly
      headerVariant="iconTitle"
      titleIcon={<FileText className="h-5 w-5 text-[#1597ff]" aria-hidden />}
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
      bodyClassName="!px-0 !py-0"
    >
      <div
        className="mx-5 mb-4 flex items-start gap-2 rounded-lg border border-[#b6d9f5] bg-[#e8f4fc] px-3 py-2.5 text-sm text-[#1e3a5f]"
        data-testid={`${tid}-info-banner`}
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#1597ff]" aria-hidden />
        <span>View or download previously generated access confirmations.</span>
      </div>

      <ul
        className="m-0 list-none divide-y divide-slate-100 p-0"
        data-testid={`${tid}-file-list`}
      >
        {sortedFiles.map((file, i) => {
          const fileId = getPolicyDeclarationFileId(file) || String(i)
          const viewBusy = processing[fileId] === 'view'
          const downloadBusy = processing[fileId] === 'download'
          const { dateLabel, timeLabel } = formatAccessConfirmationLabelsFromFilename(
            file.filename,
          )
          const title = dateLabel
            ? `Access Confirmation · ${dateLabel}`
            : file.filename || file.path || 'Access Confirmation'

          return (
            <li
              key={fileId}
              data-testid={`${tid}-row-${i}`}
              className="flex flex-col gap-3 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            >
              <div className="flex min-w-0 items-start gap-3">
                <span
                  className="inline-flex h-10 w-9 shrink-0 flex-col items-center justify-end rounded-md bg-[#1597ff] pb-1 text-[9px] font-bold leading-none tracking-wide text-white"
                  aria-hidden="true"
                >
                  <FileText className="mb-0.5 h-4 w-4 opacity-90" strokeWidth={2} />
                  PDF
                </span>
                <div className="min-w-0" data-testid={`${tid}-row-${i}-filename`}>
                  <p className="m-0 truncate text-sm font-semibold text-slate-900">{title}</p>
                  {timeLabel ? (
                    <p className="m-0 mt-0.5 text-xs text-slate-500">
                      Generated at {timeLabel}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                <button
                  type="button"
                  data-testid={`${tid}-row-${i}-view-btn`}
                  className={viewBtnClass}
                  disabled={viewBusy || downloadBusy || !file.stream_url}
                  onClick={() => void viewFile(file)}
                >
                  {viewBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden />
                  )}
                  View
                </button>
                <button
                  type="button"
                  data-testid={`${tid}-row-${i}-download-btn`}
                  className={downloadBtnClass}
                  disabled={viewBusy || downloadBusy || !file.stream_url}
                  onClick={() => void downloadFile(file)}
                >
                  {downloadBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Download className="h-4 w-4" aria-hidden />
                  )}
                  Download
                </button>
              </div>
            </li>
          )
        })}
        {sortedFiles.length === 0 ? (
          <li className="px-5 py-8 text-center text-sm text-slate-500">No files available.</li>
        ) : null}
      </ul>
    </OnScreenModal>
  )
}
